<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { debounce } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const MIN_QUERY_LENGTH = 2;

const { t } = useI18n();
const store = useStore();

const accountId = useMapGetter('getCurrentAccountId');
const conversations = useMapGetter(
  'conversationSearch/getConversationRecords'
);
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const [isOpen, toggleOpen] = useToggle();
const query = ref('');
const inputRef = ref(null);

const isFetching = computed(() => uiFlags.value.conversation.isFetching);
const trimmedQuery = computed(() => query.value.trim());
const hasQuery = computed(() => trimmedQuery.value.length >= MIN_QUERY_LENGTH);

const runSearch = debounce(
  q => store.dispatch('conversationSearch/conversationSearch', { q }),
  400,
  false
);

watch(query, () => {
  if (hasQuery.value) {
    runSearch(trimmedQuery.value);
  } else {
    store.dispatch('conversationSearch/clearSearchResults');
  }
});

const conversationUrl = id =>
  frontendURL(`accounts/${accountId.value}/conversations/${id}`);

const contactLabel = conversation => {
  const contact = conversation.contact || {};
  return (
    contact.name ||
    contact.phoneNumber ||
    contact.email ||
    t('CHAT_LIST.QUICK_SEARCH.UNNAMED')
  );
};

const open = async () => {
  toggleOpen(true);
  await nextTick();
  inputRef.value?.focus();
};

const close = () => {
  toggleOpen(false);
  query.value = '';
  store.dispatch('conversationSearch/clearSearchResults');
};

const onToggle = () => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const onResultClick = () => {
  close();
};

onBeforeUnmount(() => {
  store.dispatch('conversationSearch/clearSearchResults');
});
</script>

<template>
  <div v-on-click-outside="close" class="relative flex">
    <NextButton
      v-tooltip.top-end="$t('CHAT_LIST.QUICK_SEARCH.TOOLTIP')"
      icon="i-lucide-search"
      slate
      xs
      faded
      @click="onToggle"
    />
    <div
      v-if="isOpen"
      class="mt-1 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-80 rounded-xl p-3 absolute z-40 top-full flex flex-col gap-2"
      :class="{
        'ltr:left-0 rtl:right-0': !isOnExpandedLayout,
        'ltr:right-0 rtl:left-0': isOnExpandedLayout,
      }"
    >
      <div class="relative">
        <span
          class="absolute i-lucide-search size-3.5 top-2.5 left-3 text-n-slate-10"
        />
        <input
          ref="inputRef"
          v-model="query"
          type="search"
          :placeholder="$t('CHAT_LIST.QUICK_SEARCH.PLACEHOLDER')"
          class="reset-base w-full h-9 py-2 pl-9 pr-2 text-sm border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
          @keydown.esc="close"
        />
      </div>

      <div class="max-h-80 overflow-y-auto -mx-1 px-1">
        <div v-if="isFetching" class="flex justify-center py-4">
          <Spinner :size="20" />
        </div>

        <ul
          v-else-if="conversations.length"
          class="flex flex-col gap-0.5 list-none"
        >
          <li v-for="conversation in conversations" :key="conversation.id">
            <router-link
              :to="conversationUrl(conversation.id)"
              class="flex flex-col gap-0.5 px-2 py-2 rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
              @click="onResultClick"
            >
              <span class="text-sm text-n-slate-12 truncate">
                {{ contactLabel(conversation) }}
              </span>
              <span class="text-xs text-n-slate-11 truncate">
                #{{ conversation.id }}
                <template v-if="conversation.inbox && conversation.inbox.name">
                  · {{ conversation.inbox.name }}
                </template>
              </span>
            </router-link>
          </li>
        </ul>

        <p
          v-else-if="hasQuery"
          class="text-sm text-n-slate-11 text-center py-4 mb-0"
        >
          {{ $t('CHAT_LIST.QUICK_SEARCH.NO_RESULTS') }}
        </p>

        <p v-else class="text-sm text-n-slate-11 text-center py-4 mb-0">
          {{ $t('CHAT_LIST.QUICK_SEARCH.HINT') }}
        </p>
      </div>
    </div>
  </div>
</template>
